import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../store/auth";
import { useChat } from "../store/chat";
import { useReports } from "../store/reports";
import { useLocale } from "../hooks/useLocale";
import type { ChatSession } from "../api/types";
import { listDashboards } from "../features/dashboards/api";
import type { DashboardSummary } from "../features/dashboards/types";
import styles from "./Sidebar.module.css";

interface Props {
  onCreate: () => void;
}

const defaultDashboards: DashboardSummary[] = ["sales-overview", "stalled-leads", "first-response"].map((slug) => ({
  slug,
  title_key: `dashboards.${slug.replaceAll("-", "_")}.title`,
  description_key: `dashboards.${slug.replaceAll("-", "_")}.description`,
}));

export function Sidebar({ onCreate }: Props) {
  const { t, locale, setLocale, locales } = useLocale();
  const sessions = useChat((s) => s.sessions);
  const loadSessions = useChat((s) => s.loadSessions);
  const renameSession = useChat((s) => s.renameSession);
  const deleteSession = useChat((s) => s.deleteSession);
  const reports = useReports((s) => s.list);
  const loadReports = useReports((s) => s.loadList);
  const renameReport = useReports((s) => s.rename);
  const setFavorite = useReports((s) => s.setFavorite);
  const tenant = useAuth((s) => s.tenant);
  const currentUser = useAuth((s) => s.currentUser);
  const logout = useAuth((s) => s.logout);
  const switchTenant = useAuth((s) => s.switchTenant);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editingReport, setEditingReport] = useState<{
    id: number;
    section: "favorites" | "all";
  } | null>(null);
  const [reportEditValue, setReportEditValue] = useState("");
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const switcherRef = useRef<HTMLDivElement | null>(null);
  const [dashboardCatalogue, setDashboardCatalogue] = useState<{ tenantID: number; items: DashboardSummary[] } | null>(null);
  const dashboardItems = dashboardCatalogue && dashboardCatalogue.tenantID === tenant?.id
    ? dashboardCatalogue.items
    : defaultDashboards;

  useEffect(() => {
    if (!tenant?.id) return;
    let active = true;
    const tenantID = tenant.id;
    void listDashboards().then((items) => {
      if (active) setDashboardCatalogue({ tenantID, items });
    }).catch(() => {
      if (active) setDashboardCatalogue({ tenantID, items: defaultDashboards });
    });
    return () => { active = false; };
  }, [tenant?.id]);

  // Close the account switcher on any outside click.
  useEffect(() => {
    if (!switcherOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!switcherRef.current?.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [switcherOpen]);

  async function onSwitchTenant(id: number) {
    if (switching || id === currentUser?.tenant_id) {
      setSwitcherOpen(false);
      return;
    }
    setSwitching(true);
    try {
      const redirect = await switchTenant(id);
      // Full navigation: the session cookie now points at another workspace,
      // so every store (chat, reports, members) must re-hydrate from scratch.
      window.location.href = redirect;
    } catch (err) {
      console.error("switch tenant failed", err);
      setSwitching(false);
      setSwitcherOpen(false);
    }
  }

  // "Избранное" mirrors starred reports on top; the full list below shows
  // EVERYTHING visible to the user (own + public), stars included.
  const favoriteReports = reports.filter((r) => r.is_favorite);

  // Reports are fetched once when the sidebar mounts and refreshed on route
  // changes between /chat and /report so promote/delete actions reflect quickly.
  useEffect(() => {
    void loadReports();
  }, [loadReports, location.pathname]);

  // Sessions follow the same pattern. Loading them here (rather than in
  // ChatPage) ensures the "Recent chats" list is populated even when the user
  // lands on /report/X directly — otherwise sessions stays empty until the
  // user happens to visit /chat.
  useEffect(() => {
    void loadSessions();
  }, [loadSessions, location.pathname]);

  const activeChatId = params.sessionId ? Number(params.sessionId) : null;
  const activeReportId = params.reportId ? Number(params.reportId) : null;

  async function commitRename(id: number) {
    const next = editValue.trim();
    setEditingId(null);
    if (!next) return;
    try {
      await renameSession(id, next);
    } catch (err) {
      console.error("rename failed", err);
    }
  }

  async function commitReportRename(id: number) {
    const next = reportEditValue.trim();
    setEditingReport(null);
    if (!next) return;
    try {
      await renameReport(id, next);
    } catch (err) {
      console.error("report rename failed", err);
    }
  }

  async function onDelete(id: number) {
    if (!window.confirm(t("chat.delete_confirm"))) return;
    // Navigate away BEFORE the server delete. Otherwise the store update from
    // deleteSession can trigger a re-render where the URL still points at the
    // doomed id, and ChatPage's sync effect kicks off selectSession(id) -> 404.
    if (activeChatId === id) navigate("/chat", { replace: true });
    try {
      await deleteSession(id);
    } catch (err) {
      console.error("delete failed", err);
    }
  }

  async function onLogout() {
    const target = await logout();
    navigate(target, { replace: true });
  }

  function renderItem(s: ChatSession) {
    const editing = editingId === s.id;
    const label = s.name?.trim() || t("chat.untitled", { id: s.id });
    return (
      <li
        key={s.id}
        className={`${styles.item} ${s.id === activeChatId ? styles.itemActive : ""}`}
      >
        {editing ? (
          <form
            className={styles.renameForm}
            onSubmit={(e) => {
              e.preventDefault();
              void commitRename(s.id);
            }}
          >
            <input
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => void commitRename(s.id)}
              maxLength={255}
            />
          </form>
        ) : (
          <button
            type="button"
            className={styles.itemLink}
            onClick={() => navigate(`/chat/${s.id}`)}
            title={label}
          >
            {label}
          </button>
        )}
        <span className={styles.itemActions}>
          <button
            type="button"
            className={styles.itemAction}
            onClick={() => {
              setEditingId(s.id);
              setEditValue(s.name ?? "");
            }}
            aria-label={t("chat.rename")}
            title={t("chat.rename")}
          >
            ✎
          </button>
          <button
            type="button"
            className={styles.itemAction}
            onClick={() => void onDelete(s.id)}
            aria-label={t("chat.delete")}
            title={t("chat.delete")}
          >
            ×
          </button>
        </span>
      </li>
    );
  }

  function renderReportItem(
    r: (typeof reports)[number],
    section: "favorites" | "all",
  ) {
    const label = r.name?.trim() || t("reports.untitled", { id: r.id });
    const isActive = r.id === activeReportId;
    const editing =
      editingReport?.id === r.id && editingReport.section === section;
    const canRename =
      currentUser != null &&
      (r.owner_user_id === currentUser.id || currentUser.role === "owner");
    const starLabel = r.is_favorite
      ? t("reports.unfavorite")
      : t("reports.favorite");
    return (
      <li
        key={r.id}
        className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
      >
        {editing ? (
          <form
            className={styles.renameForm}
            onSubmit={(e) => {
              e.preventDefault();
              void commitReportRename(r.id);
            }}
          >
            <input
              autoFocus
              value={reportEditValue}
              onChange={(e) => setReportEditValue(e.target.value)}
              onBlur={() => void commitReportRename(r.id)}
              aria-label={t("reports.name")}
              maxLength={255}
            />
          </form>
        ) : (
          <button
            type="button"
            className={styles.itemLink}
            onClick={() => navigate(`/report/${r.id}`)}
            title={label}
          >
            {label}
          </button>
        )}
        {r.visibility === "public" && (
          <span className={styles.badge}>{t("reports.badge_public")}</span>
        )}
        {canRename && !editing && (
          <span className={styles.itemActions}>
            <button
              type="button"
              className={styles.itemAction}
              onClick={() => {
                setEditingReport({ id: r.id, section });
                setReportEditValue(r.name ?? "");
              }}
              aria-label={t("reports.rename")}
              title={t("reports.rename")}
            >
              ✎
            </button>
          </span>
        )}
        <button
          type="button"
          className={`${styles.star} ${r.is_favorite ? styles.starActive : ""}`}
          onClick={() =>
            setFavorite(r.id, !r.is_favorite).catch((err) =>
              console.error("toggle favorite failed", err),
            )
          }
          aria-pressed={r.is_favorite}
          aria-label={starLabel}
          title={starLabel}
        >
          {r.is_favorite ? "★" : "☆"}
        </button>
      </li>
    );
  }

  return (
    <div className={styles.sidebar}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t("app.title")}</h1>
          <div className={styles.switcher} ref={switcherRef}>
            <button
              type="button"
              className={styles.switcherToggle}
              onClick={() => setSwitcherOpen((v) => !v)}
              aria-expanded={switcherOpen}
              aria-haspopup="listbox"
              title={t("workspace.switcher_label")}
            >
              <span className={styles.switcherName}>
                {tenant?.name ?? t("workspace.switcher_none")}
              </span>
              <span className={styles.switcherCaret} aria-hidden>
                ▾
              </span>
            </button>
            {switcherOpen && (
              <div className={styles.switcherMenu} role="listbox">
                {(currentUser?.tenants ?? []).map((ws) => (
                  <button
                    key={ws.id}
                    type="button"
                    role="option"
                    aria-selected={ws.id === currentUser?.tenant_id}
                    className={styles.switcherItem}
                    disabled={switching}
                    onClick={() => void onSwitchTenant(ws.id)}
                  >
                    <span className={styles.switcherItemName} title={ws.name}>
                      {ws.name}
                    </span>
                    <span className={styles.switcherItemMeta}>
                      {t(`members.role_${ws.role}`)}
                      {ws.id === currentUser?.tenant_id && " ✓"}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  className={`${styles.switcherItem} ${styles.switcherCreate}`}
                  onClick={() => {
                    setSwitcherOpen(false);
                    navigate("/create-workspace");
                  }}
                >
                  + {t("workspace.switcher_create")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <button type="button" className={styles.newChat} onClick={onCreate}>
        + {t("chat.new_session")}
      </button>

      <nav className={styles.dashboardNav} aria-label={t("dashboards.nav")}>
        {dashboardItems.map((dashboard) => (
          <button
            key={dashboard.slug}
            type="button"
            className={`${styles.dashboardLink} ${
              location.pathname === `/dashboards/${dashboard.slug}`
                || (dashboard.slug === "sales-overview" && location.pathname === "/dashboards")
                ? styles.dashboardLinkActive
                : ""
            }`}
            onClick={() => navigate(`/dashboards/${dashboard.slug}`)}
          >
            <span className={`${styles.dashboardMark} ${
              dashboard.slug === "stalled-leads" ? styles.dashboardMarkDanger
                : dashboard.slug === "first-response" ? styles.dashboardMarkWarning : ""
            }`} aria-hidden />
            {t(dashboard.title_key)}
          </button>
        ))}
      </nav>

      <div className={styles.scroll}>
        {favoriteReports.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t("reports.favorites_section")}
            </h2>
            <ul className={styles.list}>
              {favoriteReports.map((report) =>
                renderReportItem(report, "favorites"),
              )}
            </ul>
          </section>
        )}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("reports.section_title")}</h2>
          {reports.length === 0 ? (
            <p className={styles.empty}>{t("reports.empty")}</p>
          ) : (
            <ul className={styles.list}>
              {reports.map((report) => renderReportItem(report, "all"))}
            </ul>
          )}
        </section>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("chat.history_section")}</h2>
          {sessions.length === 0 ? (
            <p className={styles.empty}>{t("chat.no_sessions")}</p>
          ) : (
            <ul className={styles.list}>{sessions.map(renderItem)}</ul>
          )}
        </section>
      </div>

      {currentUser?.role === "owner" && (
        <button
          type="button"
          className={styles.membersLink}
          onClick={() => navigate("/settings/members")}
        >
          {t("members.nav_link")}
        </button>
      )}

      <footer className={styles.footer}>
        <div className={styles.langToggle}>
          {locales.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={l === locale}
              onClick={() => setLocale(l)}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={styles.logoutBtn}
          onClick={() => void onLogout()}
        >
          {t("auth.signout")}
        </button>
      </footer>
    </div>
  );
}
